import merge from 'lodash/merge';
import { resolve } from 'url';

import { debugAPI } from 'Lib/debug';
import type { FetchOptions } from 'Lib/utils/fetch';
import { now } from 'Lib/utils/perfTimer';
import type { GenericProtobufMessage } from 'Lib/utils/protobufUtils';

import type { ApiOptions, Fetch } from './ApiClient.types';

/* TODO: @antonyc figure out if we will break anything if use the version of the app here instead
  this should help us with debugging esp when we are tracing in kibana logs
*/
const HEADER_TEST_ID = 'testId';
const HEADER_SUPPORT_SHORT_VIDEO = 'support-short-video';

function buildUrl(base: string, path: string) {
  const isHttpBase = base && base.indexOf('http') >= 0;

  if (!isHttpBase) {
    return resolve('', base + path);
  }
  return resolve(base, path);
}

const safelyParseResponse = async (response: Response) => {
  let json;
  try {
    json = await response.json();
  } catch (e) {
    return response;
  }
  return json;
};

/**
 * API Client for calling Tinder's backend services
 * @type {ApiClient}
 */
class ApiClient {
  acceptLanguage: string | undefined;
  apiBase: string;
  apiToken: string | undefined;
  build: number | undefined;
  deviceId?: string | null;
  fetchLib: Fetch;
  getAcceptLanguage: Function | undefined;
  getLocale: Function | undefined;
  getSessionData: Function | undefined;
  getTestId?: Function | null;
  handleTokenReceived: Function | undefined;
  isAuthorized: boolean;
  locale: string;
  platformHeader: string | undefined;
  useWebp: boolean;
  version: string | undefined;

  /**
   * Create an instance of a ApiClient
   * @param  {Object} [options={}] Options for configuring the API client
   * @param  {string} options.locale The 'locale' to append to each API request url (e.g. 'en-US')
   * @param  {string} options.apiBase The base url for each API request
   * @param  {string} options.locale The locale used for each API request
   * @param  {string} options.fetch The fetch library to use
   * @param  {string} options.onTokenReceived Optional callback that is called when a new apiToken is obtained
   * @param  {string} options.getTestId testId is optional and will be included in the header if it's a test environment
   * @param  {string} options.apiToken Bootstrapped token to use for each API request
   * @param  {string} options.platformHeader Platform in the request header
   * @param  {number} options.build Numeric build version to be the request headers (e.g. '1020406')
   * @param  {string} options.version Actual semver version to be the request headers (e.g. '2.4.6')
   * @return {ApiClient}
   */
  constructor({
    acceptLanguage,
    apiBase,
    apiToken,
    build,
    deviceId,
    fetch,
    getAcceptLanguage,
    getLocale,
    getSessionData,
    getTestId,
    locale,
    onTokenReceived,
    platformHeader,
    useWebp,
    version,
  }: ApiOptions) {
    this.acceptLanguage = acceptLanguage;
    this.apiBase = apiBase;
    this.apiToken = apiToken;
    this.build = build;
    this.deviceId = deviceId;
    this.fetchLib = fetch;
    this.getAcceptLanguage = getAcceptLanguage;
    this.getLocale = getLocale;
    this.getSessionData = getSessionData;
    this.getTestId = getTestId;
    this.handleTokenReceived = onTokenReceived;
    this.isAuthorized = false;
    this.locale = locale;
    this.platformHeader = platformHeader;
    this.useWebp = !!useWebp;
    this.version = version;
  }

  /**
   * Returns the default (most common) options needed for each API request
   * @param  {Object} [overrides={}] An object of overrides for the options. Uses lodash merge to perform a deep merge
   * @return {[type]}                The result of the merge between the common options and the overrides
   */
  getDefaultOptions(overrides: Partial<FetchOptions> = {}) {
    const locale = this.getLocale?.() ?? this.locale;
    // Backend not always honor `locale` query param, but they always read accept-language
    const acceptLanguage = this.getAcceptLanguage?.() ?? this.acceptLanguage;

    const { appSessionId, appSessionTimeElapsed, userSessionId, userSessionTimeElapsed } =
      this.getSessionData?.() ?? {};

    return merge(
      {
        headers: {
          'accept-language': acceptLanguage,
          'app-session-id': appSessionId,
          'app-session-time-elapsed': appSessionTimeElapsed,
          'app-version': this.build, // confusing header name for historical reasons (:
          'persistent-device-id': this.deviceId,
          'tinder-version': this.version,
          'user-session-id': userSessionId,
          'user-session-time-elapsed': userSessionTimeElapsed,
          'x-supported-image-formats': this.useWebp ? 'webp,jpeg' : 'jpeg',
          Accept: 'application/json',
          platform: this.platformHeader,
        },
        qs: {
          locale,
        },
      },
      overrides
    );
  }

  /**
   * Sets the API token for use with each authed request
   * @param {string} apiToken The apiToken
   */
  setApiToken(apiToken: string) {
    this.apiToken = apiToken;
    if (this.handleTokenReceived) {
      this.handleTokenReceived(apiToken);
    }
    this.isAuthorized = true;
  }

  /**
   * Convenience method to update the locale during app runtime
   * @param {string} locale The updated locale to use for each API request
   */
  setLocale(locale: string) {
    this.locale = locale;
  }

  /**
   * Clear the API token
   */
  async logout() {
    this.setApiToken('');
  }

  /**
   * Lowest level fetch wrapper that is eventually called by the rest of the
   * convenience methods (fetchWithAuth, post, get, etc...). This method should
   * only be used if you need to bypass the normal error handling and/or don't need
   * auth.
   * @param  {string}  path    URL path for the api endpoint. This will be combined with config.apiBase
   * @param  {Object}  options API options for the fetch method
   * @param  {number}  timeout Timeout for a request. Default is none and will defer to fetch library
   * @return {Promise}
   */
  async fetch(
    path: string,
    options?: FetchOptions,
    timeout?: number,
    processErrorResponse: boolean = true
  ): Promise<any> {
    const url = buildUrl(this.apiBase, path);
    const fetchOptions = this.getDefaultOptions(options);
    const { shouldMeasurePerf, shouldSupportShortVideo } = fetchOptions;

    let { fetchLib } = this;

    if (this.getTestId) {
      fetchOptions.headers[HEADER_TEST_ID] = this.getTestId();
    }

    if (shouldMeasurePerf) {
      fetchLib = this.fetchWithPerf.bind(this);
    }

    if (shouldSupportShortVideo) {
      fetchOptions.headers[HEADER_SUPPORT_SHORT_VIDEO] = '1';
    }

    const response = await fetchLib(url, fetchOptions, timeout);

    if (!response || !processErrorResponse) {
      return response;
    }

    // 401 we will handle as a reauth
    if (response.status === 400 || response.status > 401) {
      const json = await safelyParseResponse(response);

      return Promise.resolve({
        json: () =>
          Promise.resolve({
            data: json,
            status: response.status,
            error: json.error,
            url,
            ok: response.ok,
          }),
      });
    }

    return response;
  }

  /**
   * Wraps the fetch call with performance timings
   * @param  {string}  url    URL path for the api endpoint
   * @param  {Object}  fetchOptions API options for the fetch method
   * @param  {number}  timeout Timeout for a request. Default is none and will defer to fetch library
   * @return {Promise} Response object decorated with performance timings
   */
  async fetchWithPerf(url: string, options?: FetchOptions, timeout?: number): Promise<any> {
    const start = now();

    const response = await this.fetchLib(url, options, timeout);

    response.perf = {
      rt: now() - start,
      path: url,
      method: options?.method,
    };

    return response;
  }

  /**
   * Same as fetch but will check whether or not the API client is authorized. This will return the raw
   * API response and you will need to convert the data to JSON yourself. This should only be used if
   * you are not expecting a JSON response from the backend
   * @param  {string}  path    URL path for the api endpoint. This will be combined with config.apiBase
   * @param  {Object}  options API options for the fetch method
   * @param  {number}  timeout Timeout for a request. Default is none and will defer to fetch library
   * @return {Promise}
   */
  async fetchWithAuthRaw(
    path: string,
    options?: FetchOptions,
    timeout?: number,
    processErrorResponse?: boolean
  ): Promise<any> {
    const authOptions = options;
    if (this.apiToken) {
      merge(authOptions, {
        headers: {
          'X-Auth-Token': this.apiToken,
        },
      });
    }

    const response = await this.fetch(path, authOptions, timeout, processErrorResponse);
    return response;
  }

  /**
   * Same as fetchWithAuthRaw but provides a JSON response and/or text response
   * @param  {string}  path    URL path for the api endpoint. This will be combined with config.apiBase
   * @param  {Object}  options API options for the fetch method
   * @param  {number}  timeout Timeout for a request. Default is none and will defer to fetch library
   * @return {Promise}         [description]
   */
  async fetchWithAuth(path: string, options?: FetchOptions, timeout?: number): Promise<any> {
    const response = await this.fetchWithAuthRaw(path, options, timeout);
    if (!response) {
      return response;
    }
    const json = await safelyParseResponse(response);

    if (response.perf) {
      json.perf = response.perf;
    }
    return json;
  }

  async fetchProtoWithAuth(
    path: string,
    requestSchema: GenericProtobufMessage | undefined,
    responseSchema: GenericProtobufMessage,
    data?: any,
    options?: FetchOptions,
    timeout?: number
  ): Promise<any> {
    let encodedData;
    if (requestSchema) {
      encodedData = requestSchema.encode(data).finish();

      if (debugAPI.enabled) {
        const decodedRequest = requestSchema.decode(encodedData).toJSON();
        const decodedRequestObj = requestSchema.toObject(decodedRequest, {
          enums: String,
          defaults: true,
        });
        debugAPI('Proto Request', decodedRequestObj);
      }
    }

    const fetchOptions = merge(
      {
        headers: {
          Accept: 'application/x-protobuf',
          'Content-Type': 'application/x-protobuf',
        },
        body: encodedData,
      },
      options
    );
    const response = await this.fetchWithAuthRaw(path, fetchOptions, timeout, false);
    const buffer = await response.arrayBuffer();
    const decodedMessage = responseSchema.decode(new Uint8Array(buffer));
    const decodedObject = responseSchema.toObject(decodedMessage, {
      enums: String,
      defaults: true,
    });
    decodedObject.status = response.status;

    debugAPI('Proto Response', decodedObject);

    return decodedObject;
  }

  /**
   * Convenience GET request method that will call the API endpoint with Auth
   * @param  {string}  path    URL path for the api endpoint. This will be combined with config.apiBase
   * @param  {Object}  data    Request data to be included with the GET request as URL params
   * @param  {Object}  options API options for the fetch method
   * @return {Promise}         Returns a fulfilled promise with the JSON response
   */
  async get(path: string, data?: any, options?: FetchOptions): Promise<any> {
    const fetchOptions = {
      method: 'GET',
      qs: data,
      ...options,
    };
    return this.fetchWithAuth(path, fetchOptions);
  }

  /**
   * GET request method for protobuf data that will call the API endpoint with Auth
   * @param  {string}  path       URL path for the api endpoint. This will be combined with config.apiBase
   * @param  {Object}  responseSchema  the protobuf response schema definiton
   * @param  {Object}  data       Request data to be included with the GET request as URL params
   * @param  {Object}  options    API options for the fetch method
   * @return {Promise}            Returns a fulfilled promise with the response decoded to JSON
   */
  async getProtobuf(
    path: string,
    responseSchema: GenericProtobufMessage,
    data?: any,
    options?: FetchOptions
  ): Promise<any> {
    const fetchOptions = {
      qs: data,
      ...options,
    };

    return this.fetchProtoWithAuth(path, undefined, responseSchema, undefined, fetchOptions);
  }

  /**
   * Convenience PUT request method that will call the API endpoint with Auth
   * @param  {string}  path    URL path for the api endpoint. This will be combined with config.apiBase
   * @param  {Object}  data    Request data to be included with the PUT request as part of the body
   * @param  {Object}  options API options for the fetch method
   * @return {Promise}         Returns a fulfilled promise with the JSON response
   */
  async put(path: string, data?: any, options?: FetchOptions): Promise<any> {
    const fetchOptions = merge(
      {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PUT',
        body: JSON.stringify(data),
      },
      options
    );
    return this.fetchWithAuth(path, fetchOptions);
  }

  /**
   * PUT request method for protobuf data that will call the API endpoint with Auth
   * @param  {string}  path       URL path for the api endpoint. This will be combined with config.apiBase
   * @param  {Object}  requestSchema  the protobuf request schema definiton
   * @param  {Object}  responseSchema  the protobuf response schema definiton
   * @param  {Object}  data       Request data in JSON to be included with the PUT request
   * @param  {Object}  options    API options for the fetch method
   * @return {Promise}            Returns a fulfilled promise with the response decoded to JSON
   */
  async putProtobuf(
    path: string,
    requestSchema: GenericProtobufMessage,
    responseSchema: GenericProtobufMessage,
    data?: any,
    options?: FetchOptions
  ): Promise<any> {
    const fetchOptions = {
      method: 'PUT',
      ...options,
    };

    return this.fetchProtoWithAuth(path, requestSchema, responseSchema, data, fetchOptions);
  }

  /**
   * Convenience POST request method that will call the API endpoint with Auth
   * @param  {string}  path    URL path for the api endpoint. This will be combined with config.apiBase
   * @param  {Object}  data    Request data to be included with the POST request as part of the body
   * @param  {Object}  options API options for the fetch method
   * @return {Promise}         Returns a fulfilled promise with the JSON response
   */
  async post(path: string, data?: any | any[], options?: FetchOptions): Promise<any> {
    const fetchOptions = merge(
      {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      },
      options
    );

    if (data) {
      fetchOptions.body = JSON.stringify(data);
    }

    return this.fetchWithAuth(path, fetchOptions);
  }

  /**
   * Convenience POST request method that will call the API endpoint with Auth and a FormData body
   * @param  {string}  path    URL path for the api endpoint. This will be combined with config.apiBase
   * @param  {FormData}  data    Request data to be included with the POST request as part of the body
   * @param  {Object}  options API options for the fetch method
   * @return {Promise}         Returns a fulfilled promise with the JSON response
   */
  async postFormData(path: string, data?: FormData, options?: FetchOptions): Promise<any> {
    const fetchOptions = merge(
      {
        headers: {},
        method: 'POST',
      },
      options
    );

    if (data) {
      fetchOptions.body = data;
    }

    return this.fetchWithAuth(path, fetchOptions);
  }

  /**
   * POST request method for protobuf data that will call the API endpoint with Auth
   * @param  {string}  path       URL path for the api endpoint. This will be combined with config.apiBase
   * @param  {Object}  requestSchema  the protobuf request schema definiton
   * @param  {Object}  responseSchema  the protobuf response schema definiton
   * @param  {Object}  data       Request data in JSON to be included with the POST request
   * @param  {Object}  options    API options for the fetch method
   * @return {Promise}            Returns a fulfilled promise with the response decoded to JSON
   */
  async postProtobuf(
    path: string,
    requestSchema: GenericProtobufMessage,
    responseSchema: GenericProtobufMessage,
    data?: any,
    options?: FetchOptions
  ): Promise<any> {
    const fetchOptions = {
      method: 'POST',
      ...options,
    };

    return this.fetchProtoWithAuth(path, requestSchema, responseSchema, data, fetchOptions);
  }

  /**
   * Convenience DELETE request method that will call the API endpoint with Auth
   * @param  {string}  path    URL path for the api endpoint. This will be combined with config.apiBase
   * @param  {Object}  data    Request data to be included with the GET request as part of the body
   * @param  {Object}  options API options for the fetch method
   * @return {Promise}         Returns a fulfilled promise with the JSON response
   */
  async delete(path: string, data?: any, options?: FetchOptions): Promise<any> {
    const fetchOptions = merge(
      {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'DELETE',
        body: JSON.stringify(data),
      },
      options
    );
    return this.fetchWithAuth(path, fetchOptions);
  }

  /**
   * DELETE request method for protobuf data that will call the API endpoint with Auth
   * @param  {string}  path       URL path for the api endpoint. This will be combined with config.apiBase
   * @param  {Object}  requestSchema  the protobuf request schema definiton
   * @param  {Object}  responseSchema  the protobuf response schema definiton
   * @param  {Object}  data       Request data in JSON to be included with the DELETE request
   * @param  {Object}  options    API options for the fetch method
   * @return {Promise}            Returns a fulfilled promise with the response decoded to JSON
   */
  async deleteProtobuf(
    path: string,
    requestSchema: GenericProtobufMessage,
    responseSchema: GenericProtobufMessage,
    data?: any,
    options?: FetchOptions
  ): Promise<any> {
    const fetchOptions = {
      method: 'DELETE',
      ...options,
    };

    return this.fetchProtoWithAuth(path, requestSchema, responseSchema, data, fetchOptions);
  }

  /**
   * Convenience PATCH request method that will call the API endpoint with Auth
   * @param  {string}  path    URL path for the api endpoint. This will be combined with config.apiBase
   * @param  {Object}  data    Request data to be included with the PATCH request as part of the body
   * @param  {Object}  options API options for the fetch method
   * @return {Promise}         Returns a fulfilled promise with the JSON response
   */
  async patch(path: string, data?: any | any[], options?: FetchOptions): Promise<any> {
    const fetchOptions = merge(
      {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
      },
      options
    );

    if (data) {
      fetchOptions.body = JSON.stringify(data);
    }

    return this.fetchWithAuth(path, fetchOptions);
  }

  /**
   * PATCH request method for protobuf data that will call the API endpoint with Auth
   * @param  {string}  path       URL path for the api endpoint. This will be combined with config.apiBase
   * @param  {Object}  requestSchema  the protobuf request schema definiton
   * @param  {Object}  responseSchema  the protobuf response schema definiton
   * @param  {Object}  data       Request data in JSON to be included with the PATCH request
   * @param  {Object}  options    API options for the fetch method
   * @return {Promise}            Returns a fulfilled promise with the response decoded to JSON
   */
  async patchProtobuf(
    path: string,
    requestSchema: GenericProtobufMessage,
    responseSchema: GenericProtobufMessage,
    data?: any,
    options?: FetchOptions
  ): Promise<any> {
    const fetchOptions = {
      method: 'PATCH',
      ...options,
    };

    return this.fetchProtoWithAuth(path, requestSchema, responseSchema, data, fetchOptions);
  }
}

export default ApiClient;
